/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable jsdoc/require-jsdoc */
import { castId, normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Brand, Model } from '@studio/models/device';
import { toHexFromId } from '#~/api/index.js';
import { toStatus } from '@studio/models/device/stream';
import { formatQuery, formatUri, parseQuery, parseUri, toPath, toPort } from '@studio/models/net';
import { Protocol, toConfiguration, toProtocol, toUri, VideoDefaults } from '@studio/models/video';
import type { Video } from '@studio/models/video';
import { toConfiguration as toNdiConfig } from '@studio/models/video/ndi';
import { toConfiguration as toIrisConfig } from '@studio/models/video/iris';
import { toConfiguration as toLocalConfig } from '@studio/models/video/local';
import { Limits } from '@studio/models/video/preset';
import type { Preset } from '@studio/models/video/preset';
import type { Configuration as RtspConfig } from '@studio/models/video/rtsp';
import { isRtsp } from '@studio/models/video/rtsp';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type {
  StudioDeviceVideoImage,
  StudioDeviceVideoImagePreset
} from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { isNotNil, toNumber, toString } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { DeviceOptions, PbDevice, PbStatusDiscovery, PbStudioDevice } from './types.js';
import type { Registrant } from '#~/api/device/registry/index.js';
import type { Discovery$Video } from '#~/device/discovery/index.js';
import type { Device } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';
import { logger } from '#~/logger/index.js';

type PbStudioDevicePresets = DeepPartial<StudioDeviceVideoImage['presets']>;

/* JS => PB */
const jsToPbStudioDeviceVideoPresets = (
  jsPresets: Video['presets']
): T_Maybe<PbStudioDevicePresets> => {
  if (!jsPresets) {
    return undefined;
  }

  return Object.fromEntries(
    Object.values(jsPresets)
      .map(
        (preset): T_Maybe<[string, DeepPartial<StudioDeviceVideoImagePreset>]> => [
          preset.id,
          {
            deviceSettings: Object.fromEntries(
              Object.entries(preset.deviceSettings).map(([key, value]) => [key, value])
            ),
            name: preset.name.slice(0, Limits.name),
            rank: preset.rank,
            thumbnail: preset.thumbnail
          }
        ]
      )
      .filter(isNotNil)
  );
};

export const jsToPbStudioDeviceVideo = (
  device: Device,
  { liveKit }: DeviceOptions
): PbStudioDevice['video'] => {
  const { video } = device;

  if (!video) {
    return undefined;
  }

  return {
    image: {
      presets: jsToPbStudioDeviceVideoPresets(video.presets) ?? {}
    },
    uri: formatUri(toUri(video.config, { liveKit }))
  };
};

export const pbToJsDiscoveryVideo = (
  agentId: Agent['id'],
  pbDiscovery: PbStatusDiscovery
): T_Maybe<Omit<Discovery$Video, 'id'>> => {
  const { local, network, source } = pbDiscovery;

  if (source && network) {
    // Panasonic discovery supplies a model/host, not a playable transport.
    // Only identified rows can use the registry's RTSP template.
    const protocol =
      source === 'panasonic'
        ? pbDiscovery.modelId?.length || pbDiscovery.brandId?.length
          ? Protocol.RTSP
          : undefined
        : toProtocol(source);

    if (!protocol) {
      return undefined;
    }

    if (protocol === Protocol.NDI) {
      const video = toNdiConfig(parseUri(pbDiscovery.meta));

      if (!video) {
        return undefined;
      }

      // Carry brand_id/model_id when the encoder propagated them onto
      // this NDI entry from a same-host rtsp fingerprint sibling (see
      // ApplySameHostEnrichment in same_host_merge.go). Without this,
      // the NDI row never participates in the known-model shortcut
      // even though the encoder did its part.
      //
      // Mirrors the RTSP branch below: useBrands() resolves Brand from
      // model_id, panel.tsx gates the known-model shortcut on
      // selection.modelId. NDI uses the same template/control contract
      // as RTSP, only the video URI differs.
      const modelId = pbDiscovery.modelId?.length
        ? toHexFromId<Model['id']>(pbDiscovery.modelId)
        : undefined;
      const host = network?.host ?? '';

      return {
        agentId,
        name: video.name,
        ...(host ? { address: host } : {}),
        ...(modelId ? { modelId } : {}),
        video
      };
    }

    // RTSP discoveries from fingerprint matching: carry brand/model/modelId for the UI
    // and add-device flow. The URX is built from the device registry template on add.
    if (protocol === Protocol.RTSP) {
      const host = network?.host ?? '';
      const name = pbDiscovery.brand ?? pbDiscovery.model ?? host;
      const modelId = pbDiscovery.modelId?.length
        ? toHexFromId<Model['id']>(pbDiscovery.modelId)
        : undefined;
      // Brand-level fingerprint match: the encoder sends `brandId` (and the
      // brand name) with NO `modelId` when it matches a brand but not a
      // specific model (see matcher.go MatchMDNS/MatchONVIF brand fallback).
      // Carry both through so the discovery table's Brand column can resolve
      // a brand-only match — without this it stays empty because the table
      // resolves brand exclusively via `modelId → template.model.brandId`.
      const brandId = pbDiscovery.brandId?.length
        ? toHexFromId<Brand['id']>(pbDiscovery.brandId)
        : undefined;
      return {
        agentId,
        name,
        address: host,
        ...(modelId ? { modelId } : {}),
        ...(brandId ? { brandId } : {}),
        ...(pbDiscovery.brand ? { brand: pbDiscovery.brand } : {}),
        video: { host, port: network?.port ?? 554, path: '', protocol: Protocol.RTSP }
      };
    }

    // Unknown sources stay undefined so old firmware that publishes a
    // source Studio does not map does not crash the selector.
    // Per rule: wire-contract-changes-must-not-break-old-firmware
    // (ai.knowledge/global) — missing/unknown source is not fatal.
    if (protocol === Protocol.IRIS) {
      const video = toIrisConfig(parseUri(pbDiscovery.meta));

      return video
        ? {
            agentId,
            name: video.name,
            video,
            irisEnabled: true,
            address: video.host,
            ...(video.modelId ? { modelId: castId<Model['id']>(video.modelId) } : {}),
            ...(video.version ? { version: video.version } : {})
          }
        : undefined;
    }
  }

  if (source && local) {
    const video = toLocalConfig(parseUri(pbDiscovery.meta, local.api ?? 'avfvideosrc'));

    return video && local.name
      ? {
          agentId,
          name: local.name,
          video
        }
      : undefined;
  }

  return undefined;
};

const pbToJsVideoPresets = (pbVideoPresets: T_Maybe<PbStudioDevicePresets>): Video['presets'] => {
  if (!pbVideoPresets) {
    return undefined;
  }

  const results = Object.fromEntries(
    Object.entries(pbVideoPresets)
      .map(([key, pbPreset]): T_Maybe<[Preset['id'], Preset]> => {
        const pbValue = pbPreset;

        if (!pbValue) {
          return undefined;
        }

        const id = normalizeId<Preset['id']>(key);
        const name = toString(pbValue.name)?.slice(0, Limits.name);
        const { thumbnail } = pbValue;

        const rank = toNumber(pbValue.rank, { default: Number.MAX_SAFE_INTEGER });

        const deviceSettings = <Preset['deviceSettings']>Object.fromEntries(
          Object.entries(
            <Record<string, StudioDeviceVideoImagePreset>>(pbValue.deviceSettings ?? {})
          )
            .map(([key, pbValue]) => [key, pbValue])
            .filter(isNotNil)
        );

        if (!id || !name) {
          return undefined;
        }

        return [
          id,
          {
            deviceSettings,
            id,
            name,
            rank,
            thumbnail
          }
        ];
      })
      .filter(isNotNil)
  );

  return <Record<Preset['id'], Preset>>results;
};

export const pbToJsVideo = (pbDevice: PbDevice): T_Maybe<Video> => {
  const { pbStatus, pbStudio } = pbDevice;

  const pbStudioVideo = pbStudio?.video;
  const pbStatusVideo = pbStatus?.video;

  const studioUri = parseUri(pbStudioVideo?.uri);
  const statusUri = parseUri(pbStatusVideo?.status?.uri);

  /*
   * If the video URI reported by the encoder is different from the one that
   * we have in the studio, we should use the one from the encoder.
   */
  const config =
    studioUri && statusUri
      ? (toConfiguration({
          ...statusUri,
          query: formatQuery({ ...parseQuery(statusUri.query), ...parseQuery(studioUri.query) })
        }) ?? toConfiguration(studioUri))
      : toConfiguration(studioUri);

  if (!config) {
    return undefined;
  }

  const status = pbStatusVideo?.status;

  return defaults(
    { config },
    {
      error: toString(status?.error),
      hasConnected: status?.hasConnected,
      network: toNumber(status?.network),
      presets: pbToJsVideoPresets(pbStudioVideo?.image?.presets),
      status: toStatus(status?.state)
    },
    VideoDefaults
  );
};

export const pbToJsVideoTemplate = ({ config }: Registrant): Template['video'] => {
  const protocol = toProtocol(config.videoDriver);

  if (!protocol) {
    return undefined;
  }

  if (isRtsp(protocol)) {
    /*
     * ENG-4159 RTSP Assisted Setup: seed the registry template with every
     * field the assisted RTSP screen consumes. videoHost was previously
     * dropped, so toUri/composeRtspUrl returned undefined without a host. Also
     * read the Sub stream (videoPathSub/videoPortSub); the Sub port falls back
     * to the Main port when unset (toPort -> undefined for an unset 0). These
     * are pre-existing generated proto fields (device_settings.pb) — a pure
     * client READ, NOT a schema change (rule never-modify-generated).
     */
    const port = toPort(config.videoPort);

    return {
      host: toString(config.videoHost),
      path: toPath(config.videoPath),
      pathSub: toPath(config.videoPathSub),
      port,
      portSub: toPort(config.videoPortSub) ?? port,
      protocol
    } satisfies T_Optional<RtspConfig>;
  }

  logger.warning(`@fixme auto-config: ${protocol}`);

  return undefined;
};
